/**
 * Busca de endereço com autocomplete do Google Places (Places API New): campo de texto livre que
 * sugere enquanto se digita e, ao escolher uma sugestão, devolve o endereço já estruturado
 * (logradouro/número/bairro/cidade/UF/CEP) + coordenadas. Complementa o preenchimento por CEP —
 * os dois coexistem no formulário.
 *
 * Só aparece com `VITE_GOOGLE_MAPS_API_KEY` E a Places API habilitada na chave; qualquer falha
 * (chave sem Places, rede) esconde o campo e o cadastro segue normalmente pelo CEP.
 *
 * ⚠️ LGPD: cada trecho digitado aqui é enviado ao Google em tempo real. É um SKU pago por sessão
 * (mais caro que geocoding) — por isso é um campo opcional, não o caminho principal.
 */
import { useEffect, useRef, useState } from 'react';
import { garantirOpcoes, importLibrary } from '@/lib/mapas';

export type EnderecoEscolhido = {
  logradouro?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
  cep?: string;
  latitude?: string;
  longitude?: string;
};

/** Primeiro componente cujo `type` casa (na ordem dada); `curto` pega a sigla (ex. UF "SP"). */
function achar(
  comps: google.maps.places.AddressComponent[],
  tipos: string[],
  curto = false,
): string | undefined {
  for (const t of tipos) {
    const c = comps.find((x) => x.types.includes(t));
    if (c) return (curto ? c.shortText : c.longText) ?? undefined;
  }
  return undefined;
}

function extrair(place: google.maps.places.Place): EnderecoEscolhido {
  const comps = place.addressComponents ?? [];
  const loc = place.location;
  return {
    logradouro: achar(comps, ['route']),
    numero: achar(comps, ['street_number']),
    bairro: achar(comps, ['sublocality_level_1', 'sublocality', 'neighborhood']),
    cidade: achar(comps, ['locality', 'administrative_area_level_2']),
    uf: achar(comps, ['administrative_area_level_1'], true),
    cep: achar(comps, ['postal_code']),
    latitude: loc ? String(loc.lat()) : undefined,
    longitude: loc ? String(loc.lng()) : undefined,
  };
}

export function BuscaEnderecoGoogle({
  apiKey,
  onEscolher,
}: {
  apiKey: string;
  onEscolher: (e: EnderecoEscolhido) => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [indisponivel, setIndisponivel] = useState(false);
  // Callback fresco sem remontar o web component a cada render do formulário.
  const onEscolherRef = useRef(onEscolher);
  onEscolherRef.current = onEscolher;

  useEffect(() => {
    let cancelado = false;
    let el: google.maps.places.PlaceAutocompleteElement | null = null;
    garantirOpcoes(apiKey);

    async function montar() {
      const { PlaceAutocompleteElement } = await importLibrary('places');
      if (cancelado || !hostRef.current) return;
      el = new PlaceAutocompleteElement({ includedRegionCodes: ['br'] });
      el.style.width = '100%';
      el.addEventListener('gmp-select', async (ev) => {
        try {
          const { place } = await ev.placePrediction
            .toPlace()
            .fetchFields({ fields: ['addressComponents', 'location'] });
          onEscolherRef.current(extrair(place));
        } catch {
          // Uma escolha que falhou não pode derrubar o formulário — segue pelo CEP.
        }
      });
      hostRef.current.appendChild(el);
    }

    montar().catch(() => {
      if (!cancelado) setIndisponivel(true);
    });

    return () => {
      cancelado = true;
      el?.remove();
    };
  }, [apiKey]);

  if (indisponivel) return null;
  return (
    <div className="flex flex-col gap-1">
      <div ref={hostRef} />
      <span className="tipo-corpo-pequeno text-text-tertiary">
        Buscar pelo Google — ao escolher, os campos abaixo se preenchem
      </span>
    </div>
  );
}
